import React from 'react';
import { Alert, Button, Stack, Tooltip, Typography } from '@mui/material';

import { TrussEnvelopeInsertionInformation } from '../../../../../library/layout-geometry/truss-envelopes/insertion-information';
import { TOGGLE_LOCK_TO_BASE_HOTKEY } from '../../../../../store/actions/globalHotkeyActions';
import { getPickingObjectInfo } from '../../../../../store/selectors/pickingSelectors';
import {
  applicationOnPickingLayoutPoint,
  resetInsertionTrussEnvelope,
} from '../../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { FlipInsertionTrussEnvelopeButton } from './FlipTrussEnvelopeButton';
import { LayoutLinePickerField } from './LayoutLinePickerField';
import { LayoutPointPickerField } from './LayoutPointPickerField';
import { LockedTrussEnvelopeTargetControl } from './LockedTrussEnvelopeTargetControl';

export function TrussEnvelopeInsertionPointControls(props: {
  insertion: TrussEnvelopeInsertionInformation;
  bothPointsChosen: boolean;
}) {
  const dispatch = useAppDispatch();
  const allowReset = props.insertion.data.leftPoint && props.insertion.data.rightPoint;

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  const isPickingTargetForLocking =
    pickingObjectInfo?.type === 'pickingLayoutLine' && pickingObjectInfo.target === 'parallel-snapping-base-line';

  return (
    <Stack spacing={2}>
      {props.bothPointsChosen || props.insertion.activeMode === 'point-selection' ? (
        <Stack spacing={2}>
          <LayoutPointPickerField
            label="Left Point"
            pickButtonVariant="text"
            target="insertion-envelope-left"
            value={props.insertion.data.leftPoint}
          />

          <FlipInsertionTrussEnvelopeButton insertion={props.insertion} />

          <LayoutPointPickerField
            label="Right Point"
            pickButtonVariant="text"
            target="insertion-envelope-right"
            value={props.insertion.data.rightPoint}
          />
        </Stack>
      ) : (
        <Stack direction="column" spacing={2}>
          <Alert variant="outlined" severity="info" sx={{ textWrap: 'wrap' }}>
            Pick your desired envelope location in the scene to the left. Use the snapping settings to customize.
          </Alert>

          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', alignItems: 'baseline' }}>
            {props.insertion.settings.snapping.lockedTarget ? (
              <LockedTrussEnvelopeTargetControl
                sx={{ padding: 1, marginTop: 2 }}
                lockedTarget={props.insertion.settings.snapping.lockedTarget}
              />
            ) : (
              <>
                <Typography variant="body2" color="textSecondary">
                  {isPickingTargetForLocking ? 'currently picking base (click to cancel):' : 'or:'}
                </Typography>

                <Tooltip disableInteractive title={`(Ctrl + ${TOGGLE_LOCK_TO_BASE_HOTKEY.toUpperCase()})`}>
                  <span>
                    <LayoutLinePickerField
                      showOnlyButton
                      buttonText="Lock to Base"
                      target="parallel-snapping-base-line"
                    />
                  </span>
                </Tooltip>

                {!isPickingTargetForLocking && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      dispatch(applicationOnPickingLayoutPoint({ target: 'insertion-envelope-left' }));
                    }}
                  >
                    Pick Points Individually
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Stack>
      )}

      {allowReset && props.insertion.activeMode !== 'point-selection' && (
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(resetInsertionTrussEnvelope());
          }}
        >
          Pick Envelope Again
        </Button>
      )}
    </Stack>
  );
}
